import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import React from 'react';
import styled, { css } from 'styled-components';
import ProgressLoadingIndicator from '../ProgressLoadingIndicator';
import useTabIndex from '../useTabIndex';
import withThemeProps from '../withThemeProps';
var ButtonElement = styled.button.withConfig({
  displayName: "Button__ButtonElement",
  componentId: "sc-ffozwa-0"
})(["\n  position: relative;\n  font-family: inherit;\n  font-size: 1rem;\n  font-weight: normal;\n  text-decoration: none;\n  :hover {\n    text-decoration: none;\n  }\n  ", ";\n"], _ref => {
  var $buttonStyle = _ref.$buttonStyle;
  return $buttonStyle;
});
var Label = styled.span.withConfig({
  displayName: "Button__Label",
  componentId: "sc-ffozwa-1"
})(["\n  [data-loading] > & {\n    /* Use visibility instead of unmounting the label or using display: none so\n     * that the button's width as determined by the label doesn't change. */\n    ", "\n  }\n  ", "\n"], _ref2 => {
  var $isLoadingLabelVisible = _ref2.$isLoadingLabelVisible;
  return $isLoadingLabelVisible ? css(["\n            visibility: visible;\n            margin-left: 8px;\n          "]) : css(["\n            visibility: hidden;\n          "]);
}, _ref3 => {
  var $labelStyle = _ref3.$labelStyle;
  return $labelStyle;
});
export var DefaultLoadingIndicator = styled(ProgressLoadingIndicator).attrs({
  'data-button-loading': '',
  fillStyle: {
    minWidth: 4,
    borderRadius: 2
  },
  variant: 'button'
}).withConfig({
  displayName: "Button__DefaultLoadingIndicator",
  componentId: "sc-ffozwa-2"
})(["\n  ", "\n"], _ref4 => {
  var $isLoadingLabelVisible = _ref4.$isLoadingLabelVisible;
  return !$isLoadingLabelVisible && css(["\n      position: absolute;\n      top: 50%;\n      left: 0;\n      right: 0;\n      padding: 0 12px;\n      transform: translate3d(0, -50%, 0);\n    "]);
});
DefaultLoadingIndicator.displayName = 'Button.DefaultLoadingIndicator';

/**
 * A basic button component with theme support. All additional props besides
 * those documented are passed along to the underlying DOM element.
 *
 * The component can also render links that will use the same styling as your
 * theme’s buttons if you pass the `href` prop.
 */
export var Button = /*#__PURE__*/React.forwardRef((_ref5, ref) => {
  var buttonStyle = _ref5.buttonStyle,
      children = _ref5.children,
      href = _ref5.href,
      _ref5$isLoading = _ref5.isLoading,
      isLoading = _ref5$isLoading === void 0 ? false : _ref5$isLoading,
      _ref5$isLoadingLabelV = _ref5.isLoadingLabelVisible,
      isLoadingLabelVisible = _ref5$isLoadingLabelV === void 0 ? false : _ref5$isLoadingLabelV,
      labelStyle = _ref5.labelStyle,
      _ref5$loadingIndicato = _ref5.loadingIndicator,
      loadingIndicator = _ref5$loadingIndicato === void 0 ? /*#__PURE__*/React.createElement(DefaultLoadingIndicator, null) : _ref5$loadingIndicato,
      role = _ref5.role,
      tabIndex = _ref5.tabIndex,
      tagName = _ref5.tagName,
      type = _ref5.type,
      rest = _objectWithoutProperties(_ref5, ["buttonStyle", "children", "href", "isLoading", "isLoadingLabelVisible", "labelStyle", "loadingIndicator", "role", "tabIndex", "tagName", "type"]);

  var buttonType = type || 'button';

  if (href) {
    tagName = 'a';
    buttonType = undefined;
  } else if (!role) {
    role = 'button';
  }

  tabIndex = useTabIndex(tabIndex);
  return /*#__PURE__*/React.createElement(ButtonElement, _extends({
    as: tagName,
    $buttonStyle: buttonStyle,
    "data-loading": isLoading ? '' : undefined,
    href: href,
    ref: ref,
    role: role,
    tabIndex: tabIndex,
    type: buttonType,
    $isLoadingLabelVisible: isLoadingLabelVisible
  }, rest), isLoading ? loadingIndicator : null, /*#__PURE__*/React.createElement(Label, {
    $labelStyle: labelStyle,
    $isLoadingLabelVisible: isLoadingLabelVisible
  }, children));
});
Button.displayName = 'Button';
export default withThemeProps(Button, {
  section: 'button',
  defaultVariant: 'primary'
});